/* ============================================
   FUZE THEME - Global JavaScript
   ============================================ */

// --- Cart API ---
class CartAPI {
  static async get() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  static async add(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Could not add to cart');
    }
    return response.json();
  }

  static async update(updates) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return response.json();
  }

  static async change(line, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    return response.json();
  }

  static async clear() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    return response.json();
  }
}

// --- Section Rendering API ---
async function fetchSection(sectionId, url = window.location.pathname) {
  const response = await fetch(`${url}?sections=${sectionId}`);
  const data = await response.json();
  return data[sectionId];
}

// --- Event Bus ---
const FuzeEvents = {
  emit(event, detail = {}) {
    document.dispatchEvent(new CustomEvent(`fuze:${event}`, { detail, bubbles: true }));
  },
  on(event, callback) {
    document.addEventListener(`fuze:${event}`, callback);
  },
  off(event, callback) {
    document.removeEventListener(`fuze:${event}`, callback);
  }
};

// --- Utility Functions ---
function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

function throttle(fn, limit = 100) {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

function formatMoney(cents, format = '${{amount}}') {
  const amount = (cents / 100).toFixed(2);
  return format.replace('{{amount}}', amount)
    .replace('{{amount_no_decimals}}', Math.round(cents / 100))
    .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
}

function trapFocus(container, initialFocusEl) {
  const focusable = container.querySelectorAll(
    'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])'
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (initialFocusEl) {
    initialFocusEl.focus();
  } else if (first) {
    first.focus();
  }

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  container.addEventListener('keydown', handleKeydown);
  return () => container.removeEventListener('keydown', handleKeydown);
}

// --- Cart Count Update ---
function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = count;
    el.closest('[data-cart-count-bubble]')?.classList.toggle('hidden', count === 0);
  });
}

// --- Global Cart Event Handling ---
FuzeEvents.on('cart:updated', async (e) => {
  const cart = e.detail.cart || await CartAPI.get();
  updateCartCount(cart.item_count);
});

// --- Scroll Lock ---
function lockScroll() {
  document.body.style.overflow = 'hidden';
  document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
}

function unlockScroll() {
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
}

// --- Export to window ---
window.FuzeTheme = {
  CartAPI,
  fetchSection,
  FuzeEvents,
  debounce,
  throttle,
  formatMoney,
  trapFocus,
  updateCartCount,
  lockScroll,
  unlockScroll
};
